"use client";

import { notify } from "@/components/ui/dialogs";
import { DatePicker } from "@/components/ui/date-picker";
import { useState, useEffect } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  UserIcon,
  Shield01Icon,
  SquareLock02Icon,
  Mail01Icon,
  Call02Icon,
  ViewIcon,
  ViewOffIcon,
  ArrowRight01Icon,
  Location01Icon,
  Briefcase01Icon,
  Calendar01Icon,
} from "@hugeicons/core-free-icons";
import { get, put } from "../utils/service";

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  position: string;
  department: string;
  role: string;
  timezone: string;
  language: string;
  avatar_url: string;
  created_at: string;
  last_login: string;
  gender?: string;
  date_of_birth?: string;
  location?: string;
}


const SkeletonCard = () => (
  <div className="animate-pulse bg-white rounded-lg p-6">
    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-10 bg-gray-200 rounded"></div>
      ))}
    </div>
  </div>
);

function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  useEffect(() => {
    fetchSettingsData();
  }, []);

  const fetchSettingsData = async () => {
    setLoading(true);
    try {
      const profileResponse = await get<{ success: boolean; data: UserProfile }>('/auth/me');
      if (profileResponse.success) {
        setProfile(profileResponse.data);
      }
    } catch (error) {
      console.error('Error fetching settings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfileField = (field: string, value: string) => {
    if (profile) {
      setProfile(prev => prev ? { ...prev, [field]: value } : null);
    }
  };

  const saveProfileChanges = async () => {
    if (!profile) return;

    setSaving(true);
    try {
      const response = await put<{ success: boolean }>('/auth/profile', {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone || '',
        position: profile.position || '',
        department: profile.department || '',
        gender: profile.gender || '',
        date_of_birth: profile.date_of_birth || '',
        location: profile.location || ''
      });

      if (response.success) {
        notify('Profile updated successfully');
      }
    } catch {
      notify('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwords.new !== passwords.confirm) {
      notify("New passwords don't match");
      return;
    }

    try {
      const response = await put<{ success: boolean }>('/auth/password', {
        current_password: passwords.current,
        new_password: passwords.new,
        confirm_password: passwords.confirm
      });

      if (response.success) {
        notify('Password updated successfully');
        setPasswords({ current: '', new: '', confirm: '' });
      }
    } catch {
      notify('Error updating password');
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const menuItems = [
    { id: 'basic', label: 'Basic Information', icon: UserIcon },
    { id: 'security', label: 'Password & Security', icon: SquareLock02Icon }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="w-full">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <SkeletonCard />
            </div>
            <div className="lg:col-span-3">
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">

      <div className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-4">
              <nav className="space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs transition-colors ${
                        activeTab === item.id
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <HugeiconsIcon icon={Icon} size={16} />
                      <span className="flex-1 text-left">{item.label}</span>
                      <HugeiconsIcon icon={ArrowRight01Icon} size={12} />
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            {activeTab === 'basic' && profile && (
              <div className="space-y-6">
                <div className="bg-primary rounded-lg p-6">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary text-sm font-bold">
                        {getInitials(profile.first_name, profile.last_name)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xs font-bold text-white mb-2">
                        {profile.first_name} {profile.last_name}
                      </h2>
                      <p className="text-xs text-gray-300 mb-3">
                        {profile.position || 'Position'} • {profile.department || 'Department'}
                      </p>
                      <div className="flex flex-wrap gap-4 text-xs text-gray-300">
                        <div className="flex items-center gap-2">
                          <HugeiconsIcon icon={Mail01Icon} size={12} />
                          <span>{profile.email}</span>
                        </div>
                        {profile.phone && (
                          <div className="flex items-center gap-2">
                            <HugeiconsIcon icon={Call02Icon} size={12} />
                            <span>{profile.phone}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <HugeiconsIcon icon={Shield01Icon} size={12} />
                          <span className="capitalize">{profile.role}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <h2 className="text-xs font-bold text-gray-900 mb-6">Basic Information</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={UserIcon} size={12} className="text-gray-400" />
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profile.first_name}
                        onChange={(e) => updateProfileField('first_name', e.target.value)}
                        placeholder="First Name"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={UserIcon} size={12} className="text-gray-400" />
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profile.last_name}
                        onChange={(e) => updateProfileField('last_name', e.target.value)}
                        placeholder="Last Name"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={Mail01Icon} size={12} className="text-gray-400" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profile.email}
                        disabled
                        placeholder="email@example.com"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={Call02Icon} size={12} className="text-gray-400" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profile.phone || ''}
                        onChange={(e) => updateProfileField('phone', e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={UserIcon} size={12} className="text-gray-400" />
                        Gender
                      </label>
                      <select
                        value={profile.gender || ''}
                        onChange={(e) => updateProfileField('gender', e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={Calendar01Icon} size={12} className="text-gray-400" />
                        Date of Birth
                      </label>
                      <DatePicker value={profile.date_of_birth || ''} onChange={(v) => updateProfileField('date_of_birth', v)} max={new Date().toISOString().slice(0, 10)} clearable />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={Briefcase01Icon} size={12} className="text-gray-400" />
                        Position
                      </label>
                      <input
                        type="text"
                        value={profile.position || ''}
                        onChange={(e) => updateProfileField('position', e.target.value)}
                        placeholder="Your Position"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={Briefcase01Icon} size={12} className="text-gray-400" />
                        Department
                      </label>
                      <input
                        type="text"
                        value={profile.department || ''}
                        onChange={(e) => updateProfileField('department', e.target.value)}
                        placeholder="Your Department"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={Location01Icon} size={12} className="text-gray-400" />
                        Location
                      </label>
                      <input
                        type="text"
                        value={profile.location || ''}
                        onChange={(e) => updateProfileField('location', e.target.value)}
                        placeholder="City, Country"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={saveProfileChanges}
                      disabled={saving}
                      className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? 'Updating...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}


            {activeTab === 'security' && (
              <div className="bg-white rounded-lg p-6">
                <h2 className="text-xs font-bold text-gray-900 mb-6">Password & Security</h2>

                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={SquareLock02Icon} size={12} className="text-gray-400" />
                        Enter Current Password
                      </label>
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwords.current}
                        onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <HugeiconsIcon icon={ViewOffIcon} size={16} /> : <HugeiconsIcon icon={ViewIcon} size={16} />}
                      </button>
                    </div>

                    <div className="relative">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <HugeiconsIcon icon={SquareLock02Icon} size={12} className="text-gray-400" />
                        Enter new password
                      </label>
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwords.new}
                        onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <HugeiconsIcon icon={ViewOffIcon} size={16} /> : <HugeiconsIcon icon={ViewIcon} size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                      <HugeiconsIcon icon={SquareLock02Icon} size={12} className="text-gray-400" />
                      Confirm new password
                    </label>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwords.confirm}
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <HugeiconsIcon icon={ViewOffIcon} size={16} /> : <HugeiconsIcon icon={ViewIcon} size={16} />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handlePasswordChange}
                    disabled={!passwords.current || !passwords.new || !passwords.confirm}
                    className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Update
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;